import { PageHero } from "@/components/PageHero";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { Marquee } from "@/components/Marquee";
import { CtaBand } from "@/sections/CtaBand";
import { IMG } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { Seo } from "@/components/Seo";

export default function About() {
  const { t, lang } = useLang();
  const a = t.about;
  return (
    <main data-testid="about-page">
      <Seo
        title="Our Story — Family-Run LA Produce Wholesaler Since 1968 | Sebastian's Produce"
        description="Three generations at the same Los Angeles dock since 1968. How Sebastian's Produce sources, grades and delivers fresh vegetables and fruit to food manufacturers and restaurants."
      />
      <PageHero key={lang} eyebrow={a.eyebrow} lines={a.lines} text={a.text} image={IMG.market} testId="about-hero" />

      <section className="py-20 lg:py-28" data-testid="about-timeline-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeader eyebrow={a.timelineEyebrow} title={a.timelineTitle} />
          <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-[var(--line)] bg-[var(--line)] md:grid-cols-2 lg:grid-cols-4">
            {a.timeline.map((tl, i) => (
              <Reveal key={tl.year} delay={i * 0.1} className="group bg-[var(--canvas)] p-8 transition-colors duration-500 hover:bg-white" data-testid={`timeline-${i}`}>
                <p className="font-display text-5xl font-bold text-[var(--green)]/30 transition-colors duration-500 group-hover:text-[var(--green)]">{tl.year}</p>
                <h3 className="font-display mt-8 text-xl font-semibold leading-tight">{tl.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--ink-2)]">{tl.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee dark />

      <section className="py-20 lg:py-28" data-testid="about-values-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow={a.valuesEyebrow} title={a.valuesTitle} text={a.valuesText} />
            <Reveal delay={0.2} className="img-zoom mt-10 overflow-hidden rounded-3xl">
              <img src={IMG.stillLife} alt="Seasonal vegetables" className="aspect-[4/3] w-full object-cover" />
            </Reveal>
          </div>
          <Stagger className="grid gap-4 self-start sm:grid-cols-2 lg:col-span-7" stagger={0.1}>
            {a.values.map((v, i) => (
              <Item key={i}>
                <div className={`h-full rounded-3xl p-8 ${i === 0 ? "bg-[var(--green)] text-white" : i === 3 ? "bg-[var(--gold)]" : "border border-[var(--line)] bg-white"}`} data-testid={`value-${i}`}>
                  <p className="font-mono-label text-[10px] opacity-70">0{i + 1}</p>
                  <h3 className="font-display mt-6 text-2xl font-bold leading-tight">{v.h}</h3>
                  <p className={`mt-3 text-sm leading-relaxed ${i === 0 ? "text-white/80" : "text-[var(--ink-2)]"}`}>{v.t}</p>
                </div>
              </Item>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="pb-20 lg:pb-28" data-testid="about-fleet-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="img-zoom relative overflow-hidden rounded-[32px] bg-[var(--ink)]">
            <img src={IMG.trucks} alt="Sebastian's Produce refrigerated trucks" className="aspect-[16/9] w-full object-cover md:aspect-[21/9]" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/10" />
            <div className="absolute inset-0 flex flex-col justify-end p-8 text-white lg:p-14">
              <p className="eyebrow !text-[var(--lime)]">{a.fleetEyebrow}</p>
              <h2 className="font-display mt-3 max-w-xl text-3xl font-bold leading-[1.02] sm:text-4xl lg:text-5xl">{a.fleetTitle}</h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">{a.fleetText}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28" data-testid="about-customers-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="rounded-[32px] bg-[var(--canvas-2)] p-10 lg:p-16">
            <p className="eyebrow">{a.whoEyebrow}</p>
            <div className="mt-6 grid gap-8 lg:grid-cols-2">
              <h2 className="font-display text-3xl font-bold leading-[1.02] sm:text-4xl">{a.whoTitle}</h2>
              <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-[15px] text-[var(--ink-2)]">
                {a.who.map((c) => (
                  <li key={c} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[var(--red)]" />{c}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
